import React from 'react'
import { Placeholder, Table } from 'react-bootstrap'
import { Link } from 'react-router-dom'

import SalesCard from '../SalesCard/SalesCard'
import SalesWarehouseBreakdown from '../SalesWarehouseBreakdown/SalesWarehouseBreakdown'
import { useSelector } from '../../../modules/store/customSelector'
import { adminRouteAlias, getAdminNavigationPath } from '../../../router/adminRouteAlias'
import { colors } from '../../../constants/salesStyles'
import { getCurrencySymbol } from '../../../utils/getCurrencySymbol'

import styles from './styles.module.scss'

interface IProps {
    columnLayout?: boolean
}

const skeletonRows = [0, 1, 2, 3]

const WarehouseStockSkeleton = () => (
    <div className={styles.skeleton} aria-hidden>
        {skeletonRows.map(row => (
            <div key={row} className={styles.skeletonRow}>
                <Placeholder animation="wave" className={styles.skeletonWave}>
                    <Placeholder xs={12} className={styles.skeletonBar} />
                </Placeholder>
                <Placeholder animation="wave" className={styles.skeletonWave}>
                    <Placeholder xs={12} className={styles.skeletonBar} />
                </Placeholder>
                <Placeholder animation="wave" className={styles.skeletonWave}>
                    <Placeholder xs={12} className={styles.skeletonBar} />
                </Placeholder>
                <Placeholder animation="wave" className={styles.skeletonWave}>
                    <Placeholder xs={12} className={styles.skeletonBar} />
                </Placeholder>
            </div>
        ))}
    </div>
)

const SalesCardWarehouseStock = ({ columnLayout }: IProps) => {
    const userSummary = useSelector(state => state.sales.userSummary)
    const isLoading = useSelector(state => state.sales.loaders.userSummary)
    const canShowFinanceInfo = useSelector(state => state.userData.perms?.canShowFinanceInfo)
    const currentCurrency = useSelector(state => state.partList.currentCurrency)
    const currencySymbol = getCurrencySymbol(currentCurrency)

    const formatCount = (value: number) => `${(value || 0).toLocaleString('ru-RU')} шт`
    const formatSum = (value: number) => `${(value || 0).toLocaleString('ru-RU')} ${currencySymbol}`

    if (!canShowFinanceInfo) {
        return null
    }

    return (
        <SalesCard
            width={columnLayout ? '100%' : 'auto'}
            widthInPixels={columnLayout ? undefined : 1180}
            innerTitle={'Весь склад'}
            marginBottomPixels={columnLayout ? 0 : 60}
            fillHeight={columnLayout}
        >
            <div className={styles.content}>
                {isLoading ? (
                    <WarehouseStockSkeleton />
                ) : userSummary.hasWarehouses ? (
                    <SalesWarehouseBreakdown items={userSummary.warehousesBreakdown || []} />
                ) : (
                    <Table size="sm" className={styles.summaryTable}>
                        <thead>
                            <tr>
                                <th></th>
                                <th className={styles.alignRight}>Товарные позиции</th>
                                <th className={styles.alignRight}>Кол-во</th>
                                <th className={styles.alignRight}>Сумма</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Наличие</td>
                                <td className={styles.alignRight}>{formatCount(userSummary.totalCount)}</td>
                                <td className={styles.alignRight}>{formatCount(userSummary.totalCountWithQty)}</td>
                                <td className={styles.alignRight}>{formatSum(userSummary.totalSum)}</td>
                            </tr>
                            <tr>
                                <td>Продано</td>
                                <td className={styles.alignRight}>{formatCount(userSummary.soldPositions)}</td>
                                <td className={styles.alignRight}>{formatCount(userSummary.soldCount)}</td>
                                <td className={styles.alignRight}>{formatSum(userSummary.soldSum)}</td>
                            </tr>
                        </tbody>
                    </Table>
                )}
                <Link
                    to={getAdminNavigationPath(adminRouteAlias.warehouses.location)}
                    className={styles.manageLink}
                    style={{ color: colors.blue }}
                >
                    Управление складами
                </Link>
            </div>
        </SalesCard>
    )
}

export default SalesCardWarehouseStock
